import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Archive, AppWindow, Check, Layers3, Pencil, Plus, Server } from "lucide-react";
import { useEffect, useState } from "react";

import { DeveloperApplicationDetail, DeveloperEnvironment, api, formatDate } from "../api";
import { EmptyState, ErrorState, LoadingState, MutationMessage, PageHeader, Section, StatusBadge } from "../account/components";
import { DeveloperPageProps } from "./DeveloperCenter";
import { useApplicationInventory } from "./hooks";

export function ApplicationsPage({ organization, assurance }: DeveloperPageProps) {
  const inventory = useApplicationInventory(organization.id);
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const canManage = organization.permissions.includes("applications.manage") && Boolean(assurance?.recent);
  useEffect(() => {
    const values = inventory.data ?? [];
    if (!values.some((item) => item.application.id === selected)) setSelected(values[0]?.application.id ?? null);
  }, [inventory.data, selected]);
  const detail = inventory.data?.find((item) => item.application.id === selected);

  return (
    <>
      <PageHeader eyebrow="APPLICATIONS" title="应用与环境" description="应用是 OIDC、动态 Claim 和原生客户端策略的产品边界。" action={canManage ? <button className="primary-button" type="button" onClick={() => setCreating(!creating)}><Plus />新建应用</button> : undefined} />
      {creating && <CreateApplication organizationId={organization.id} onCreated={(id) => { setSelected(id); setCreating(false); }} />}
      <Section title="应用清单" description="永久 key 不会在归档后释放；创建应用会自动建立 Development 环境。">
        {inventory.isPending && <LoadingState />}
        {inventory.error && <ErrorState error={inventory.error} retry={() => void inventory.refetch()} />}
        {inventory.data?.length === 0 && <EmptyState icon={AppWindow} title="尚未创建应用" detail="创建第一个应用后再配置 OAuth client、Scope 与设备策略。" />}
        {(inventory.data?.length ?? 0) > 0 && (
          <div className="developer-resource-layout">
            <div className="developer-resource-list" role="listbox" aria-label="应用">
              {inventory.data?.map((item) => (
                <button className={selected === item.application.id ? "active" : ""} role="option" aria-selected={selected === item.application.id} key={item.application.id} type="button" onClick={() => setSelected(item.application.id)}>
                  <span className="resource-glyph"><AppWindow aria-hidden="true" /></span>
                  <span><strong>{item.application.displayName}</strong><small>{item.application.key} · {item.environments.length} 个环境</small></span>
                </button>
              ))}
            </div>
            {detail && <ApplicationPanel organizationId={organization.id} detail={detail} canManage={canManage} />}
          </div>
        )}
      </Section>
    </>
  );
}

function CreateApplication({ organizationId, onCreated }: { organizationId: string; onCreated: (id: string) => void }) {
  const client = useQueryClient();
  const [key, setKey] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const create = useMutation({
    mutationFn: () => api<DeveloperApplicationDetail>(`/api/v1/organizations/${organizationId}/applications`, {
      method: "POST",
      body: JSON.stringify({ key, displayName, description: description || null }),
    }),
    onSuccess: (value) => {
      void client.invalidateQueries({ queryKey: ["developer-application-inventory", organizationId] });
      onCreated(value.application.id);
    },
  });
  return (
    <section className="creation-band developer-creation-band">
      <div><strong>创建应用</strong><span>Key 将进入审计、SDK 配置和长期运维记录。</span></div>
      <form onSubmit={(event) => { event.preventDefault(); create.mutate(); }}>
        <input required pattern="[A-Za-z][A-Za-z0-9-]{2,47}" maxLength={48} placeholder="customer-portal" value={key} onChange={(event) => setKey(event.target.value)} />
        <input required maxLength={100} placeholder="应用显示名称" value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
        <input maxLength={500} placeholder="用途说明（可选）" value={description} onChange={(event) => setDescription(event.target.value)} />
        <button className="primary-button" disabled={create.isPending} type="submit"><Plus />创建</button>
      </form>
      <MutationMessage error={create.error} />
    </section>
  );
}

function ApplicationPanel({ organizationId, detail, canManage }: { organizationId: string; detail: DeveloperApplicationDetail; canManage: boolean }) {
  const client = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [addingEnvironment, setAddingEnvironment] = useState(false);
  const [displayName, setDisplayName] = useState(detail.application.displayName);
  const [description, setDescription] = useState(detail.application.description ?? "");
  useEffect(() => {
    setDisplayName(detail.application.displayName);
    setDescription(detail.application.description ?? "");
    setEditing(false);
    setAddingEnvironment(false);
  }, [detail.application.id, detail.application.description, detail.application.displayName]);
  const invalidate = () => client.invalidateQueries({ queryKey: ["developer-application-inventory", organizationId] });
  const update = useMutation({
    mutationFn: () => api<DeveloperApplicationDetail>(`/api/v1/organizations/${organizationId}/applications/${detail.application.id}`, {
      method: "PATCH",
      body: JSON.stringify({ displayName, description: description || null }),
    }),
    onSuccess: () => { void invalidate(); setEditing(false); },
  });
  const archive = useMutation({
    mutationFn: () => api<void>(`/api/v1/organizations/${organizationId}/applications/${detail.application.id}`, { method: "DELETE" }),
    onSuccess: () => void invalidate(),
  });

  return (
    <div className="developer-resource-detail">
      <header>
        <div>
          <span className="eyebrow">{detail.application.key}</span>
          {editing ? (
            <form className="developer-edit-form" onSubmit={(event) => { event.preventDefault(); update.mutate(); }}>
              <input required maxLength={100} value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
              <textarea maxLength={500} rows={2} value={description} onChange={(event) => setDescription(event.target.value)} />
              <div className="button-row"><button className="primary-button" type="submit" disabled={update.isPending}><Check />保存</button><button className="secondary-button" type="button" onClick={() => setEditing(false)}>取消</button></div>
            </form>
          ) : <><h2>{detail.application.displayName}</h2><p>{detail.application.description || "未填写应用说明"}</p></>}
        </div>
        {canManage && !editing && <div className="item-actions"><button className="icon-button" type="button" title="编辑应用" aria-label="编辑应用" onClick={() => setEditing(true)}><Pencil /></button><button className="icon-button danger-icon" type="button" title="归档应用" aria-label="归档应用" disabled={archive.isPending} onClick={() => { if (window.confirm(`归档 ${detail.application.displayName}？所有环境都会停止服务。`)) archive.mutate(); }}><Archive /></button></div>}
      </header>
      <MutationMessage error={update.error ?? archive.error} success={update.isSuccess && "应用已更新"} />
      <div className="developer-facts"><div><span>状态</span><StatusBadge tone="good">{detail.application.status}</StatusBadge></div><div><span>创建时间</span><strong>{formatDate(detail.application.createdAt)}</strong></div><div><span>版本</span><strong>v{detail.application.version}</strong></div></div>
      <div className="subsection-heading"><div><Layers3 /><strong>环境</strong></div>{canManage && <button className="secondary-button compact-button" type="button" onClick={() => setAddingEnvironment(!addingEnvironment)}><Plus />添加环境</button>}</div>
      {addingEnvironment && <CreateEnvironment organizationId={organizationId} applicationId={detail.application.id} onCreated={() => { void invalidate(); setAddingEnvironment(false); }} />}
      <div className="environment-list">
        {detail.environments.map((environment) => <EnvironmentRow key={environment.id} organizationId={organizationId} applicationId={detail.application.id} environment={environment} canManage={canManage} onChanged={() => void invalidate()} />)}
      </div>
    </div>
  );
}

function CreateEnvironment({ organizationId, applicationId, onCreated }: { organizationId: string; applicationId: string; onCreated: () => void }) {
  const [key, setKey] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [kind, setKind] = useState<DeveloperEnvironment["kind"]>("STAGING");
  const create = useMutation({
    mutationFn: () => api<DeveloperEnvironment>(`/api/v1/organizations/${organizationId}/applications/${applicationId}/environments`, {
      method: "POST",
      body: JSON.stringify({ key, displayName, kind }),
    }),
    onSuccess: onCreated,
  });
  return (
    <form className="inline-resource-form" onSubmit={(event) => { event.preventDefault(); create.mutate(); }}>
      <input required pattern="[A-Za-z][A-Za-z0-9-]{1,31}" maxLength={32} placeholder="staging" value={key} onChange={(event) => setKey(event.target.value)} />
      <input required maxLength={80} placeholder="环境名称" value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
      <select value={kind} onChange={(event) => setKind(event.target.value as DeveloperEnvironment["kind"])}><option value="DEVELOPMENT">Development</option><option value="STAGING">Staging</option><option value="PRODUCTION">Production</option></select>
      <button className="primary-button" disabled={create.isPending} type="submit"><Plus />创建</button>
      <MutationMessage error={create.error} />
    </form>
  );
}

function EnvironmentRow({ organizationId, applicationId, environment, canManage, onChanged }: { organizationId: string; applicationId: string; environment: DeveloperEnvironment; canManage: boolean; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState(environment.displayName);
  useEffect(() => { setDisplayName(environment.displayName); setEditing(false); }, [environment.displayName, environment.id]);
  const update = useMutation({
    mutationFn: () => api<DeveloperEnvironment>(`/api/v1/organizations/${organizationId}/applications/${applicationId}/environments/${environment.id}`, { method: "PATCH", body: JSON.stringify({ displayName }) }),
    onSuccess: () => { setEditing(false); onChanged(); },
  });
  const archive = useMutation({
    mutationFn: () => api<void>(`/api/v1/organizations/${organizationId}/applications/${applicationId}/environments/${environment.id}`, { method: "DELETE" }),
    onSuccess: onChanged,
  });
  return (
    <div className="environment-row">
      <span className="resource-glyph"><Server /></span>
      <div>{editing ? <form className="environment-edit-form" onSubmit={(event) => { event.preventDefault(); update.mutate(); }}><input required maxLength={80} value={displayName} onChange={(event) => setDisplayName(event.target.value)} /><button className="icon-button" type="submit" title="保存环境名称" aria-label="保存环境名称" disabled={update.isPending}><Check /></button></form> : <><strong>{environment.displayName}</strong><span>{environment.key} · {environment.kind} · 更新于 {formatDate(environment.updatedAt)}</span></>}</div>
      <StatusBadge tone={reviewTone(environment.productionReviewState)}>{environment.productionReviewState}</StatusBadge>
      {canManage && <div className="item-actions"><button className="icon-button" type="button" title="编辑环境" aria-label={`编辑 ${environment.displayName}`} onClick={() => setEditing(!editing)}><Pencil /></button><button className="icon-button danger-icon" type="button" title="归档环境" aria-label={`归档 ${environment.displayName}`} disabled={archive.isPending} onClick={() => { if (window.confirm(`归档环境 ${environment.displayName}？`)) archive.mutate(); }}><Archive /></button></div>}
      <MutationMessage error={update.error ?? archive.error} />
    </div>
  );
}

export function reviewTone(status: string): "neutral" | "good" | "warning" | "danger" {
  if (status === "APPROVED" || status === "NOT_REQUIRED") return "good";
  if (status === "SUSPENDED") return "danger";
  if (status === "IN_REVIEW" || status === "CHANGES_REQUESTED") return "warning";
  return "neutral";
}
